import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import useDismiss from "./useDismiss";
import styles from "./FilterDropdown.module.css";
import rangeStyles from "./DateRangeFilter.module.css";

const formatDay = (iso) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" });

function describeRange({ from, to }) {
  if (from && to) return `${formatDay(from)} – ${formatDay(to)}`;
  if (from) return `From ${formatDay(from)}`;
  if (to) return `Until ${formatDay(to)}`;
  return null;
}

// Filter button that opens a from/to date picker. `value` is { from, to } as YYYY-MM-DD strings.
export default function DateRangeFilter({ label, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value);
  const rootRef = useRef(null);
  const close = useCallback(() => setOpen(false), []);
  useDismiss(open, close, rootRef);

  useEffect(() => {
    if (open) {
      setDraft(value);
    }
  }, [open, value]);

  const summary = describeRange(value);
  const invalid = Boolean(draft.from && draft.to && draft.from > draft.to);

  const apply = (event) => {
    event.preventDefault();
    if (!invalid) {
      onChange(draft);
      setOpen(false);
    }
  };

  const clear = () => {
    onChange({ from: "", to: "" });
    setOpen(false);
  };

  return (
    <div className={styles.root} ref={rootRef}>
      <button
        type="button"
        className={`${styles.trigger} ${summary ? styles.triggerActive : ""} d-flex align-items-center`}
        onClick={() => setOpen((isOpen) => !isOpen)}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        {summary ? `${label}: ${summary}` : label}
        <ChevronDown size={24} strokeWidth={1.75} className={open ? styles.chevronOpen : ""} aria-hidden="true" />
      </button>

      {open ? (
        <form className={`${styles.menu} ${rangeStyles.panel}`} onSubmit={apply} aria-label={label}>
          <div className={`${rangeStyles.fields} d-flex`}>
            <label className={rangeStyles.field}>
              From
              <input
                type="date"
                value={draft.from}
                max={draft.to || undefined}
                onChange={(event) => setDraft((current) => ({ ...current, from: event.target.value }))}
              />
            </label>
            <label className={rangeStyles.field}>
              To
              <input
                type="date"
                value={draft.to}
                min={draft.from || undefined}
                onChange={(event) => setDraft((current) => ({ ...current, to: event.target.value }))}
              />
            </label>
          </div>
          {invalid ? <p className={rangeStyles.error}>Start date must be before end date.</p> : null}
          <div className={`${rangeStyles.footer} d-flex justify-content-end`}>
            <button type="button" className={rangeStyles.secondary} onClick={clear}>
              Clear
            </button>
            <button type="submit" className={rangeStyles.primary} disabled={invalid}>
              Apply
            </button>
          </div>
        </form>
      ) : null}
    </div>
  );
}
